import React, { FC, useState } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch } from 'react-redux';

const ChangePassword: FC = () => {
  const dispatch = useDispatch();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Common InputProps function to handle password visibility toggle
  const getPasswordInputProps = (showPassword, toggleShowPassword) => ({
    type: showPassword ? 'text' : 'password',
    InputProps: {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton onClick={toggleShowPassword} edge="end">
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      )
    }
  });

  // Regex pattern for password validation
  const passwordPattern =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/;

  // Validate the new password
  const validateNewPassword = (password: string) => {
    if (currentPassword === password) {
      setNewPasswordError(
        'New password cannot be the same as the current password.'
      );
      return false;
    } else if (!passwordPattern.test(password)) {
      setNewPasswordError(
        'Password must contain at least one number, one uppercase and lowercase letter, one special character, and at least 8 or more characters.'
      );
      return false;
    } else {
      setNewPasswordError('');
      return true;
    }
  };

  // Validate confirm password
  const validateConfirmPassword = (password: string) => {
    if (currentPassword === password) {
      setConfirmPasswordError(
        'Confirm password cannot be the same as the current password.'
      );
      return false;
    } else if (password !== newPassword) {
      setConfirmPasswordError('Passwords do not match.');
      return false;
    } else {
      setConfirmPasswordError('');
      return true;
    }
  };

  // Handle password change submission
  const handlePasswordChange = async () => {
    if (
      !newPasswordError &&
      !confirmPasswordError &&
      currentPassword &&
      newPassword &&
      confirmPassword &&
      validateNewPassword(newPassword) &&
      validateConfirmPassword(confirmPassword)
    ) {
      const payload = {
        oldPassword: currentPassword,
        newPassword: newPassword
      };

      setIsLoading(true);
      const response = await httpRequest(
        'POST',
        '/auth/update-existing-password',
        payload
      );
      setIsLoading(false);
      if (response?.status === 'success') {
        toast.success(response?.message);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else if (response.type === 'NETWORK_ERROR') {
      } else {
        toast.error(response?.message);
        dispatch(handleError(response?.message));
      }
    }
  };

  const isDisabled = () => {
    return (
      !currentPassword ||
      !newPassword ||
      !confirmPassword ||
      !!newPasswordError ||
      !!confirmPasswordError
    );
  };

  return (
    <>
      {isLoading ? (
        <LoadingOverlay />
      ) : (
        <React.Fragment>
          <Grid container gap={2} mt={2} justifyContent={'center'}>
            <Grid item xs={12} sm={12} md={6}>
              <Typography
                variant="h3"
                fontSize={'22px'}
                color={'#6E759F'}
                mb={2}
              >
                Change Password
              </Typography>

              <Box component="form" noValidate autoComplete="off">
                <TextField
                  fullWidth
                  label={
                    <span>
                      Current Password <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  type={showCurrentPassword ? 'text' : 'password'}
                  variant="outlined"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}                 
                  margin="normal"
                  {...getPasswordInputProps(showCurrentPassword, () =>
                    setShowCurrentPassword(!showCurrentPassword)
                  )}
                />
                <TextField
                  fullWidth
                  label={
                    <span>
                      New Password <span style={{ color: '#8B0000' }}>*</span>
                    </span>
                  }
                  type="password"
                  variant="outlined"
                  value={newPassword}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewPassword(value);
                    validateNewPassword(value);
                  }}
                  error={!!newPasswordError}
                  helperText={newPasswordError}
                  margin="normal"
                  {...getPasswordInputProps(showNewPassword, () =>
                    setShowNewPassword(!showNewPassword)
                  )}
                />
                <TextField
                  fullWidth
                  label={
                    <span>
                      Confirm New Password{' '}
                      <span style={{ color: '#8B0000' }}>*</span>
                    </span>
                  }
                  type="password"
                  variant="outlined"
                  value={confirmPassword}
                  onChange={(e) => {
                    const value = e.target.value;
                    setConfirmPassword(value);
                    validateConfirmPassword(value);
                  }}
                  error={!!confirmPasswordError}
                  helperText={confirmPasswordError}
                  margin="normal"
                  {...getPasswordInputProps(showConfirmPassword, () =>
                    setShowConfirmPassword(!showConfirmPassword)
                  )}
                />

                <Box mt={3} display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePasswordChange}
                    style={{ background: isDisabled() ? '#E8EBED' : null }}
                    disabled={isDisabled()}
                  >
                    Update Password
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </React.Fragment>
      )}
    </>
  );
};

export default ChangePassword;
